import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import adminClient from '@/lib/api/adminClient';
import { formatCurrency, formatDateTime } from '@/lib/utils';

const STATUS_VARIANT: Record<string, 'success' | 'destructive' | 'secondary' | 'outline'> = {
  success: 'success',
  failed: 'destructive',
  pending: 'secondary',
  refunded: 'outline',
  cancelled: 'destructive',
};

const STATUS_LABEL: Record<string, string> = {
  success: '成功',
  failed: '失敗',
  pending: '處理中',
  refunded: '退款',
  cancelled: '已取消',
};

export function PaymentsPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [providerFilter, setProviderFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'payments', page, search, statusFilter, providerFilter, dateFrom, dateTo],
    queryFn: () =>
      adminClient.get('/admin/payments', {
        params: {
          page,
          per_page: 20,
          user_email: search || undefined,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          provider: providerFilter !== 'all' ? providerFilter : undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
        },
      }).then((r) => r.data),
  });

  const totalRevenue = (data?.items || [])
    .filter((t: any) => t.status === 'success')
    .reduce((s: number, t: any) => s + t.amount, 0);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">付款紀錄</h1>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">篩選結果筆數</p>
            <p className="text-2xl font-bold">{data?.total ?? 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">篩選結果收入</p>
            <p className="text-2xl font-bold text-green-500">{formatCurrency(totalRevenue)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">本頁筆數</p>
            <p className="text-2xl font-bold">{data?.items?.length ?? 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="搜尋用戶 Email..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[130px]"><SelectValue placeholder="狀態" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">所有狀態</SelectItem>
            <SelectItem value="success">成功</SelectItem>
            <SelectItem value="pending">處理中</SelectItem>
            <SelectItem value="failed">失敗</SelectItem>
            <SelectItem value="cancelled">已取消</SelectItem>
            <SelectItem value="refunded">已退款</SelectItem>
          </SelectContent>
        </Select>
        <Select value={providerFilter} onValueChange={(v) => { setProviderFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[150px]"><SelectValue placeholder="付款方式" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">所有方式</SelectItem>
            <SelectItem value="helio">Helio</SelectItem>
            <SelectItem value="nowpayments">NOWPayments</SelectItem>
          </SelectContent>
        </Select>
        <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} className="w-[150px]" />
        <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} className="w-[150px]" />
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>用戶</TableHead>
                  <TableHead>付款方式</TableHead>
                  <TableHead>金額</TableHead>
                  <TableHead>點數</TableHead>
                  <TableHead>狀態</TableHead>
                  <TableHead>時間</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.items || []).map((tx: any) => (
                  <TableRow key={tx.id}>
                    <TableCell className="text-xs font-mono text-muted-foreground">#{tx.id}</TableCell>
                    <TableCell className="text-sm">{tx.user_email ?? `uid:${tx.user_id}`}</TableCell>
                    <TableCell><Badge variant="outline">{tx.provider}</Badge></TableCell>
                    <TableCell className="font-medium">{formatCurrency(tx.amount)} {tx.currency}</TableCell>
                    <TableCell>{tx.credits_amount.toFixed(0)}</TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[tx.status] ?? 'secondary'}>
                        {STATUS_LABEL[tx.status] ?? tx.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{formatDateTime(tx.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {data && data.total > 20 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>上一頁</Button>
          <span className="text-sm text-muted-foreground">第 {page} 頁，共 {Math.ceil(data.total / 20)} 頁（{data.total} 筆）</span>
          <Button variant="outline" size="sm" disabled={page >= Math.ceil(data.total / 20)} onClick={() => setPage(page + 1)}>下一頁</Button>
        </div>
      )}
    </div>
  );
}
